/* Central Approval Inbox — My Work Queue.
 *
 * Single command-centre that aggregates everything actionable for the
 * user across:
 *   • Critical Velocity Alerts (open only)
 *   • Pending Approvals (matrix)
 *   • Contracts Waiting For Me (chain steps)
 *   • Escalated Contracts (routing escalations)
 *   • Frozen Today (last 24h)
 *
 * Every section deep-links to the relevant Quotation detail so the user
 * can act in one click.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  AlertOctagon, ClipboardList, UserCheck, ArrowUpRight, ShieldCheck,
  RefreshCw, ChevronRight,
} from 'lucide-react';


const SECTIONS = [
  {
    key:    'critical_alerts',
    label:  'Critical Alerts',
    icon:   AlertOctagon,
    tone:   'border-rose-300 bg-rose-50 text-rose-900',
    empty:  'No critical alerts. Healthy state.',
  },
  {
    key:    'pending_approvals',
    label:  'Pending Approvals (matrix)',
    icon:   ClipboardList,
    tone:   'border-amber-300 bg-amber-50 text-amber-900',
    empty:  'No pending matrix approvals.',
  },
  {
    key:    'waiting_for_me',
    label:  'Contracts Waiting For Me',
    icon:   UserCheck,
    tone:   'border-indigo-300 bg-indigo-50 text-indigo-900',
    empty:  'Nothing waiting for your action right now.',
  },
  {
    key:    'escalated_contracts',
    label:  'Escalated Contracts',
    icon:   ArrowUpRight,
    tone:   'border-orange-300 bg-orange-50 text-orange-900',
    empty:  'No active escalations.',
  },
  {
    key:    'frozen_today',
    label:  'Frozen Contracts (last 24h)',
    icon:   ShieldCheck,
    tone:   'border-emerald-300 bg-emerald-50 text-emerald-900',
    empty:  'No contracts frozen yet today.',
  },
];


const InboxItem = ({ section, item }) => {
  const navigate = useNavigate();
  const open = () => {
    const qid = item.quotation_id || item.id;
    if (qid) navigate(`/quotations?focus=${qid}`);
  };
  if (section.key === 'critical_alerts') {
    return (
      <li onClick={open}
          data-testid={`inbox-item-${section.key}-${item.alert_id}`}
          className="cursor-pointer flex items-start gap-2 px-2 py-1.5 rounded hover:bg-white/60 border border-transparent hover:border-current text-sm">
        <span className="font-mono text-xs opacity-70">{item.quotation_no || '—'}</span>
        <span className="flex-1">{item.message}</span>
        <ChevronRight className="w-4 h-4 opacity-50 mt-0.5" />
      </li>
    );
  }
  if (section.key === 'pending_approvals') {
    return (
      <li onClick={open}
          data-testid={`inbox-item-${section.key}-${item.id}`}
          className="cursor-pointer flex items-start gap-2 px-2 py-1.5 rounded hover:bg-white/60 border border-transparent hover:border-current text-sm">
        <span className="font-mono text-xs opacity-70">{item.quotation_no || item.quotation_id?.slice(-6)}</span>
        <span className="flex-1">
          {item.required_role
            ? <>Approval required — <strong>{item.required_role.replace(/_/g, ' ')}</strong></>
            : 'Pending approval'}
          {item.tier_label && <span className="opacity-70"> · {item.tier_label}</span>}
        </span>
        {item.amount != null && (
          <span className="font-mono text-xs opacity-80">{item.amount.toLocaleString()} SAR</span>
        )}
        <ChevronRight className="w-4 h-4 opacity-50 mt-0.5" />
      </li>
    );
  }
  // chain rows (waiting_for_me, escalated)
  if (item.chain_id) {
    return (
      <li onClick={open}
          data-testid={`inbox-item-${section.key}-${item.chain_id}`}
          className="cursor-pointer flex items-start gap-2 px-2 py-1.5 rounded hover:bg-white/60 border border-transparent hover:border-current text-sm">
        <span className="font-mono text-xs opacity-70">{item.quotation_no || '—'}</span>
        <span className="flex-1">
          Step <strong>{item.current_step_label || `#${(item.current_step_index || 0) + 1}`}</strong>
          {item.current_step_mode && item.current_step_mode !== 'single' && (
            <span className="ml-1 text-xs font-mono opacity-70">({item.current_step_mode})</span>
          )}
          {item.escalated && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-orange-200 text-orange-900 border border-orange-300">escalated</span>
          )}
        </span>
        {item.amount != null && (
          <span className="font-mono text-xs opacity-80">{item.amount.toLocaleString()} SAR</span>
        )}
        <ChevronRight className="w-4 h-4 opacity-50 mt-0.5" />
      </li>
    );
  }
  // frozen_today rows
  return (
    <li onClick={open}
        data-testid={`inbox-item-${section.key}-${item.id}`}
        className="cursor-pointer flex items-start gap-2 px-2 py-1.5 rounded hover:bg-white/60 border border-transparent hover:border-current text-sm">
      <span className="font-mono text-xs opacity-70">{item.quotation_no || '—'}</span>
      <span className="flex-1">
        Frozen — <strong>{item.customer_name || ''}</strong>
        {item.approved_revision_no && (
          <span className="ml-1 opacity-70 font-mono">R{item.approved_revision_number ?? 0}</span>
        )}
      </span>
      {item.approved_total != null && (
        <span className="font-mono text-xs opacity-80">{item.approved_total.toLocaleString()} SAR</span>
      )}
      <ChevronRight className="w-4 h-4 opacity-50 mt-0.5" />
    </li>
  );
};


const Inbox = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/inbox');
      setData(r.data || null);
    } catch (e) {
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-6 space-y-6" data-testid="inbox-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            My Work Queue
            <span className="text-sm font-mono px-2 py-0.5 rounded bg-gray-900 text-white"
                  data-testid="inbox-total-work-items">
              {data?.total_work_items ?? 0}
            </span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Single command centre — every action waiting for you, across approvals, alerts, and freeze events.
          </p>
        </div>
        <button
          data-testid="inbox-refresh-button"
          onClick={refresh}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 text-sm">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {!data && loading && (
        <div className="text-sm text-gray-500" data-testid="inbox-loading">Loading…</div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const sec = data[section.key] || {count: 0, items: []};
            return (
              <div key={section.key}
                   data-testid={`inbox-section-${section.key}`}
                   className={`border rounded-lg overflow-hidden ${section.tone}`}>
                <div className="px-3 py-2 border-b border-current/30 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-semibold">
                    <Icon className="w-4 h-4" />
                    {section.label}
                  </div>
                  <span className="text-sm font-mono px-2 py-0.5 rounded bg-white/70"
                        data-testid={`inbox-section-count-${section.key}`}>
                    {sec.count}
                  </span>
                </div>
                <div className="p-2 max-h-96 overflow-y-auto">
                  {sec.items.length === 0 ? (
                    <div className="text-xs italic opacity-70 px-2 py-1">{section.empty}</div>
                  ) : (
                    <ul className="space-y-1">
                      {sec.items.map((item, i) => (
                        <InboxItem key={item.alert_id || item.id || item.chain_id || i}
                                   section={section} item={item} />
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Inbox;
